import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { useAuth } from './context/AuthContext';

// Pages
import Home from './pages/Home';
import Booking from './pages/Booking';
import BookingConfirmation from './pages/BookingConfirmation';
import About from './pages/About';
import Contact from './pages/Contact';
import Admin from './pages/Admin';
import Login from './components/auth/forms/LoginForm';
import Register from './components/auth/forms/RegisterForm';
import RegistrationSuccess from './pages/auth/RegistrationSuccess';
import VerifyEmail from './pages/auth/VerifyEmail';
import VerifyAccount from './pages/auth/VerifyAccount';
import Dashboard from './pages/Dashboard';
import BookingRequestDetail from './pages/BookingRequestDetail';
import AccountSettings from './pages/AccountSettings';
import AIPreview from './pages/AIPreview';
import Appointments from './pages/Appointments';
import DepositPayment from './pages/DepositPayment';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 2rem;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
`;

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [pathname]);

  return null;
};

const HomeRoute: React.FC = () => {
  const { isAuthenticated, hasRole } = useAuth();

  if (isAuthenticated() && hasRole('artist')) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Home />;
};

const App: React.FC = () => {
  return (
    <AppContainer>
      <Navbar />
      <MainContent>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/booking/confirmed" element={<BookingConfirmation />} />
          <Route path="/pay/deposit/:bookingId" element={<DepositPayment />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/*" element={<Admin />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-account" element={<VerifyAccount />} />
          <Route
            path="/dashboard"
            element={(
              <ProtectedRoute roles={['artist']}>
                <Dashboard />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/appointments"
            element={(
              <ProtectedRoute roles={['artist']}>
                <Appointments />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/bookings/:id"
            element={(
              <ProtectedRoute roles={['artist']}>
                <BookingRequestDetail />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/account"
            element={(
              <ProtectedRoute roles={['artist']}>
                <AccountSettings />
              </ProtectedRoute>
            )}
          />
          <Route
            path="/ai-preview"
            element={(
              <ProtectedRoute roles={['artist']}>
                <AIPreview />
              </ProtectedRoute>
            )}
          />
        </Routes>
      </MainContent>
      <Footer />
    </AppContainer>
  );
};

export default App;
